<template>
  <section class="upload-dates">
    <header>
      <div class="flow-btn">
        <button @click.prevent="$emit('back')">{{ $t('buttons.back') }}</button>
      </div>
      <div class="title">
        <h1>{{ $t('create.datesTitle') }}:</h1>
        <p>{{ $t('create.datesSubtitle') }}</p>
      </div>
      <div class="flow-btn">
        <button :class="{ 'alt-btn': !isDirty }" @click="nextStep">{{ $t(nextLabel) }}</button>
      </div>
    </header>
    <div class="content">
      <label for="go-live-date">{{ $t('create.datesGoLive') }}:</label>
      <datepicker
        v-model="goLiveDate"
        id="go-live-date"
        :format="customFormatter"
        :parser="customParserGoLive"
        placeholder="mm-dd-yyyy"
        :typeable="true"
        :disabled-dates="{ to: new Date() }"
        :open-date="tomorrow"
        :use-utc="true"
        @input="updateExpireDate"
      />

      <label for="expire-date">{{ $t('create.datesExpire') }}:</label>
      <multiselect
        v-model="expireSelected"
        :options="expireOptions"
        track-by="days"
        :placeholder="$t('labels.selectOption')"
        :searchable="false"
        :show-labels="false"
        :allow-empty="false"
        @input="updateExpireDate"
      >
        <template v-slot:singleLabel="{ option }">
          <span v-if="option.date !== undefined">
            {{ displayDate(option.date) }}
          </span>
          <span v-if="option.date !== undefined" class="light-label">
            {{ `(${$tc(option.label, option.days)})` }}
          </span>
          <span v-else>
            {{ $tc(option.label, option.days) }}
          </span>
        </template>
        <template v-slot:option="{ option }">
          <span v-if="option.date !== undefined">
            {{ displayDate(option.date) }}
          </span>
          <span v-if="option.date !== undefined" class="light-label">
            {{ `(${$tc(option.label, option.days)})` }}
          </span>
          <span v-else>
            {{ $tc(option.label, option.days) }}
          </span>
        </template>
      </multiselect>

      <datepicker
        v-model="expireDate"
        v-show="expireSelected && expireSelected.days === false"
        :format="customFormatter"
        :parser="customParserExpiration"
        placeholder="mm-dd-yyyy"
        :typeable="true"
        :disabled-dates="{ to: new Date() }"
        :open-date="tomorrow"
        :use-utc="true"
      />
    </div>
  </section>
</template>

<script>
import Datepicker from '@sum.cumo/vue-datepicker'
import '@sum.cumo/vue-datepicker/dist/vuejs-datepicker.css'
import Multiselect from 'vue-multiselect'
import 'vue-multiselect/dist/vue-multiselect.min.css'
import { SAVE_MEDIA_DATES, SAVE_MEDIA } from '@/store/actions.type'
import moment from 'moment'

export default {
  name: 'upload-dates',
  components: {
    Datepicker,
    Multiselect
  },
  data() {
    return {
      goLiveDate: null,
      expireDate: null,
      expireSelected: null,
    }
  },
  computed: {
    expireOptions() {
      const theDate = this.goLiveDate ? new Date(this.goLiveDate) : Date.UTC();
      return [{
        label: 'create.datesExpireOptions',
        days: 30,
        date: moment(theDate).utc().add(4, 'hours').add(30, 'days').subtract(4, 'hours').toDate(),
      },{
        label: 'create.datesExpireOptions',
        days: 60,
        date: moment(theDate).utc().add(4, 'hours').add(60, 'days').subtract(4, 'hours').toDate(),
      },{
        label: 'create.datesExpireOptions',
        days: 90,
        date: moment(theDate).utc().add(4, 'hours').add(90, 'days').subtract(4, 'hours').toDate(),
      },{
        label: 'create.datesExpireOptions',
        days: 120,
        date: moment(theDate).utc().add(4, 'hours').add(120, 'days').subtract(4, 'hours').toDate(),
      },{
        label: 'create.datesExpireSpecific',
        days: false,
      },{
        label: 'create.datesExpireNever',
        days: Infinity
      }];
    },
    isDirty() {
      return !!this.goLiveDate
    },
    nextLabel() {
      return this.isDirty ? 'buttons.continue' : 'buttons.skipForNow'
    },
    tomorrow() {
      const today = new Date()
      const tomorrow = new Date()
      tomorrow.setDate(today.getUTCDate() + 1)
      return tomorrow
    }
  },
  methods: {
    nextStep() {
      this.$store.dispatch(SAVE_MEDIA_DATES, {
        live: this.goLiveDate,
        expiration: this.expireDate,
        expirationSelected: this.expireSelected
      })
      this.$store.dispatch(SAVE_MEDIA, false)
      this.$emit('next')
    },
    updateExpireDate() {
      let theDate = moment()
      if (this.goLiveDate !== null) theDate = moment(this.goLiveDate).utc()
      if (this.expireSelected) {
        if (this.expireSelected.days === Infinity) {
          theDate = Infinity
        } else if (this.expireSelected.days !== false) {
          theDate.add(4, 'hours').add(this.expireSelected.days, 'days').subtract(4, 'hours')
        }
        this.expireDate = theDate.toDate()
        this.$nextTick(() => {
          this.expireSelected = this.expireOptions.find((option) => option.days === this.expireSelected.days)
        })
      }
    },
    customFormatter(date) {
      return moment.utc(date).format('dddd, MM-DD-YYYY');
    },
    customParserGoLive() {
      return this.goLiveDate;
    },
    customParserExpiration() {
      return this.expireDate;
    },
    displayDate(date) {
      return moment.utc(date).format('MM-DD-YYYY')
    }
  },
  created() {
    this.goLiveDate = this.$store.state.media.liveDate
    this.expireDate = this.$store.state.media.expirationDate
    this.expireSelected = this.$store.state.media.expirationDateSelected
    if (this.goLiveDate && this.expireDate && this.expireDate !== Infinity) {
      const diff = Math.abs(moment(this.goLiveDate).utc().diff(moment(this.expireDate).utc(), 'days'))
      const selected = this.expireOptions.find(option => option.days === diff)
      if (selected) this.expireSelected = selected
      else {
        this.expireSelected = {
          label: 'A specific date...',
          days: false,
        }
      }
    } else if (this.goLiveDate) {
      this.expireSelected = {
        label: 'Never',
        days: Infinity
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.content {
  text-align: left;
  margin: auto;
  max-width: 400px;

  label {
    display: block;
    margin-top: 45px;

    &:first-child {
      margin-top: 0;
    }
  }

  .multiselect {
    margin-top: 18px;
  }
}

.light-label {
  font: lighter 16px/21px Omnes;
  letter-spacing: 0.4px;
  color: #0B0F4D;
}
</style>
